import _ from "lodash"
import z from "zod"

import {
  CUSTOMISATION_CATEGORIES,
  GLOBAL_THEME_VARIABLES,
  SETTINGS_SCOPE,
  CustomisationCategoryWithoutNone,
  CustomisationCategory,
} from "@tfg/types/constants/communication-studio.constant"
import { BaseClassSchema } from "@tfg/types/schemas/communication-studio/classes"
import { IndividualConfiguration } from "@tfg/types/schemas/communication-studio/configurationParameters"
import { CSSVariable } from "@tfg/types/schemas/communication-studio/cssVariables"
import {
  ComponentCustomisationCategories,
  ContentFilteringProps,
  ScopedConfigurationsResponse,
} from "@tfg/types/types/communication-studio/configurations.types"
import {
  GlobalCategoryStylingParameters,
  GlobalThemeVariables,
  StylingParameters,
} from "@tfg/types/types/communication-studio/styling-parameters.types"

import { injectCssClass } from "./utils"

type SpreadStylingParametersProps<T extends CustomisationCategoryWithoutNone> =
  {
    category: T
    stylingParameters: StylingParameters
    stylingParamKeys: Array<keyof StylingParameters[T]>
  }

export class DesignStudioParametersToolkit {
  static spreadStylingParameters<T extends CustomisationCategoryWithoutNone>({
    category,
    stylingParameters,
    stylingParamKeys,
  }: SpreadStylingParametersProps<T>): React.CSSProperties {
    const categoryParams = stylingParameters[category]
    return stylingParamKeys.reduce<Partial<StylingParameters[T]>>(
      (acc, key) => ({
        ...acc,
        [key]: categoryParams[key],
      }),
      {}
    )
  }

  static getConfigurations<T extends IndividualConfiguration>(
    configuration: T,
    configurationKeys: Array<keyof T>
  ): Partial<T> {
    return configurationKeys.reduce<Partial<T>>(
      (acc, key) => ({
        ...acc,
        [key]: configuration[key],
      }),
      {}
    )
  }

  static getGlobalThemeSettings(
    params: GlobalCategoryStylingParameters
  ): GlobalThemeVariables {
    const themeVariables: GlobalThemeVariables = {}

    GLOBAL_THEME_VARIABLES.forEach((variable) => {
      const value = params[variable]
      if (!value) return
      if (DesignStudioParametersToolkit.isCssVariable(value)) {
        themeVariables[variable] = value.value
      } else if (typeof value === "string") {
        themeVariables[variable] = value
      } else {
        return
      }
    })
    return themeVariables
  }

  static isClassInstance(
    value: unknown
  ): value is z.infer<typeof BaseClassSchema> {
    return BaseClassSchema.safeParse(value).success
  }

  static isCssVariable(value: unknown): value is z.infer<typeof CSSVariable> {
    return CSSVariable.safeParse(value).success
  }

  static injectAllClassesToDocumentHead(
    styles: StylingParameters
  ): Set<string> {
    const classInstances =
      DesignStudioParametersToolkit.extractClassObjectsFromStylingParameters(
        styles
      )

    const injectedClasses = new Set<string>()

    classInstances.forEach((instance) => {
      injectCssClass(instance.name, instance.props, injectedClasses)
    })

    return injectedClasses
  }

  static extractClassObjectsFromStylingParameters(
    stylingParameters: Partial<StylingParameters>
  ): Array<z.infer<typeof BaseClassSchema>> {
    const classInstances: Array<z.infer<typeof BaseClassSchema>> = []

    function walk(value: unknown) {
      if (_.isArray(value)) {
        _.forEach(value, walk)
      } else if (_.isPlainObject(value)) {
        const parsed = BaseClassSchema.safeParse(value)
        if (parsed.success) {
          classInstances.push(parsed.data)
        } else {
          if (typeof value === "object" && value !== null) {
            _.forEach(value, walk)
          }
        }
      }
    }
    ;[]

    _.forEach(stylingParameters, (value, key) => {
      if (DesignStudioParametersToolkit.isValidCategoryObject(key, value)) {
        walk(value)
      }
    })

    return classInstances
  }

  static injectCssVariable(variable: z.infer<typeof CSSVariable>) {
    document.documentElement.style.setProperty(
      `--${variable.name}`,
      variable.value
    )
  }

  static injectCssVariablesToDocumentHead(
    stylingParameters: StylingParameters
  ) {
    const cssVariables =
      DesignStudioParametersToolkit.extractCssVariablesFromStylingParameters(
        stylingParameters
      )
    cssVariables.forEach((variable) => {
      DesignStudioParametersToolkit.injectCssVariable(variable)
    })
  }

  static extractCssVariablesFromStylingParameters(
    stylingParameters: StylingParameters
  ) {
    const cssVariables: Array<z.infer<typeof CSSVariable>> = []

    function walk(value: unknown) {
      if (_.isArray(value)) {
        value.forEach(walk)
      } else if (_.isPlainObject(value)) {
        if (DesignStudioParametersToolkit.isCssVariable(value)) {
          cssVariables.push(value)
        } else {
          Object.values(value as Record<string, unknown>).forEach(walk)
        }
      }
    }

    _.forEach(stylingParameters, (value, key) => {
      if (DesignStudioParametersToolkit.isValidCategoryObject(key, value)) {
        walk(value)
      }
    })

    return cssVariables
  }

  static validCategoryKeys = new Set(Object.values(CUSTOMISATION_CATEGORIES))

  static isValidCategoryObject(
    key: unknown,
    value: unknown
  ): value is Record<string, unknown> {
    return (
      typeof key === "string" &&
      DesignStudioParametersToolkit.validCategoryKeys.has(
        key as CustomisationCategory
      ) &&
      _.isPlainObject(value)
    )
  }

  static getCssVariableLiteralName(schema: z.ZodTypeAny): string | undefined {
    if (
      schema?._def?.typeName === "ZodObject" &&
      typeof schema._def.shape === "function"
    ) {
      const shape = schema._def.shape()
      const nameDef = shape.name?._def

      if (nameDef?.typeName === "ZodLiteral") {
        return `var(--${String(nameDef.value)})`
      }
    }

    return undefined
  }

  static extractValueFromCssVariable<
    T extends CustomisationCategoryWithoutNone,
  >(
    stylingParameters: StylingParameters,
    category: T,
    key: keyof StylingParameters[T]
  ): string | undefined {
    const cssVariable = stylingParameters[category][key]
    if (DesignStudioParametersToolkit.isCssVariable(cssVariable)) {
      return cssVariable.value
    }
    return undefined
  }

  static spreadConfigurationParameters<
    T extends keyof ComponentCustomisationCategories,
  >(
    category: T,
    configuration: ScopedConfigurationsResponse
  ): ComponentCustomisationCategories[T] & ContentFilteringProps {
    return {
      ...configuration[SETTINGS_SCOPE.COMPONENT_CUSTOMISATION][category],
      ...configuration[SETTINGS_SCOPE.CONTENT_FILTERING],
    }
  }

  static spreadContentFilteringConfiguration(
    configuration: ScopedConfigurationsResponse
  ): ContentFilteringProps {
    return configuration[SETTINGS_SCOPE.CONTENT_FILTERING]
  }

  static extractFontFamily(stylingParameters: StylingParameters) {
    const fontFamilies = []

    for (const category of Object.values(
      Object.values(CUSTOMISATION_CATEGORIES).filter(
        (category) => category !== CUSTOMISATION_CATEGORIES.NONE
      )
    )) {
      const categoryParams = stylingParameters[category]

      if (
        typeof categoryParams === "object" &&
        "fontFamily" in categoryParams
      ) {
        const fontFamily = categoryParams.fontFamily
        if (fontFamily) {
          fontFamilies.push(fontFamily)
        }
      }
    }
    return fontFamilies
  }
}
